// Deterministic seeding. Nothing in the simulation may call Math.random().
//
// The daily seed is derived from the Australia/Sydney calendar date, matching the Loom & Skullcap
// hub's standardised daily reset (Sydney midnight, DST-aware). Intl handles AEDT/AEST for us, so
// there is no offset arithmetic to get wrong.

/** FNV-1a over a string → 32-bit unsigned. Used to turn a date string into a seed. */
export function hashString(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    // h *= 16777619, kept in 32-bit range via Math.imul
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/**
 * mulberry32 — small, fast, well-distributed. Returns a function yielding [0, 1).
 * Chosen over any library PRNG so the exact bit sequence is pinned in this repo forever;
 * a dependency bump must never change what puzzle a seed produces.
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0
  return function rng(): number {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Integer in [min, max] inclusive. */
export function rngInt(rng: () => number, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1))
}

/** Float in [min, max). */
export function rngRange(rng: () => number, min: number, max: number): number {
  return min + rng() * (max - min)
}

export const DAILY_TIMEZONE = 'Australia/Sydney'

/**
 * The Sydney calendar date as YYYY-MM-DD. `en-CA` formats as ISO, which is why it's used here
 * rather than assembling parts by hand.
 */
export function dailyKey(now: Date = new Date(), timeZone: string = DAILY_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/** Seed for a given day. Namespaced so a future practice mode can reuse the same date safely. */
export function dailySeed(key: string, namespace = 'bigdog:daily'): number {
  return hashString(`${namespace}:${key}`)
}

/** Day number since launch, for the share card ("Big Dog 42"). */
export const EPOCH_KEY = '2026-08-16'

export function dayNumber(key: string, epoch: string = EPOCH_KEY): number {
  const ms = Date.parse(`${key}T00:00:00Z`) - Date.parse(`${epoch}T00:00:00Z`)
  return Math.floor(ms / 86_400_000) + 1
}
